import { rateLimitedFetch } from "../../utils/coinGeckoRateLimiter";

const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const COINGECKO_IDS = "ethereum,matic-network,binancecoin,nibiru";

let cache = null;
let cacheTimestamp = 0;
let fetchPromise = null;

/**
 * Returns { ETH, POL, BNB, NIBI } USD prices.
 * Cached for 6 hours. Non-blocking — returns stale cache while refreshing.
 */
export function getNativePrices() {
  const now = Date.now();

  // Return cache if fresh
  if (cache && now - cacheTimestamp < CACHE_TTL_MS) {
    return cache;
  }

  // Trigger background refresh (don't block)
  if (!fetchPromise) {
    fetchPromise = rateLimitedFetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${COINGECKO_IDS}&vs_currencies=usd`
    )
      .then((r) => r.json())
      .then((data) => {
        cache = {
          ETH: data?.ethereum?.usd || 0,
          POL: data?.["matic-network"]?.usd || 0,
          BNB: data?.binancecoin?.usd || 0,
          NIBI: data?.nibiru?.usd || 0,
        };
        cacheTimestamp = Date.now();
        return cache;
      })
      .catch(() => {
        // Keep stale cache on error
        return cache || { ETH: 0, POL: 0, BNB: 0, NIBI: 0 };
      })
      .finally(() => {
        fetchPromise = null;
      });
  }

  // Return stale cache immediately (or zeros if first load)
  return cache || { ETH: 0, POL: 0, BNB: 0, NIBI: 0 };
}

/**
 * React hook that returns native prices and re-renders once when they load.
 */
export function useNativePrices() {
  const { useState, useEffect } = require("react");
  const [prices, setPrices] = useState(() => getNativePrices());

  useEffect(() => {
    // If cache is fresh, we already have it
    const current = getNativePrices();
    if (current.ETH > 0) {
      setPrices(current);
      return;
    }

    // Otherwise wait for the background fetch
    let mounted = true;
    const check = setInterval(() => {
      const p = getNativePrices();
      if (p.ETH > 0 && mounted) {
        setPrices(p);
        clearInterval(check);
      }
    }, 200);

    return () => {
      mounted = false;
      clearInterval(check);
    };
  }, []);

  return prices;
}
